/**
 * The crafting dialog's state, with no DOM in it.
 *
 * Split out so the rules that matter can be tested without a browser: which
 * step the player is on, whether the current selection may be submitted, what
 * the confirmation screen says, and — the part most worth pinning — what is
 * shown after the server answers.
 *
 * The rule this module exists to hold: **the browser never computes a skill's
 * properties.** Before a craft it shows the choices back in the player's own
 * words and says the result is not decided yet; after a craft it shows only
 * fields the server actually returned. A client-side damage estimate would be
 * a second, unauthoritative answer to a question the engine already owns, and
 * it would be wrong in exactly the cases that matter — when the solver had to
 * give something up.
 */

import { STATUS_MINIMUMS_SUPERSEDED_CODE } from '../game/http-types.js';
import type { CraftOptionView, CraftAxis } from '../game/skill-crafting.js';
import type { CraftParameterView } from '../game/craft-shapes.js';
import type { SkillSummaryDamage, SkillSummaryView } from '../game/skill-summary.js';
import {
  CRAFT_POWER_MIN_MULTIPLE,
  CRAFT_POWER_PRESETS,
  CRAFT_POWER_STEP,
  DEFAULT_CRAFT_POWER_MULTIPLE,
  type CraftPowerPreset,
} from '../game/craft-power.js';

/** How long the power must sit still before a preview is requested. */
export const CRAFT_PREVIEW_DEBOUNCE_MS = 250;

/** The power multiple is always stated in the frame it is chosen in. */
export function powerMultipleLabel(multiple: number): string {
  return `숙련 10 기준 내 기본 공격의 ${multiple}배`;
}

const round = (value: number, digits = 1): number => Math.round(value * 10 ** digits) / 10 ** digits;
const seconds = (value: number): string => `${round(value, 2)}초`;
const GATE_LABELS: Readonly<Record<string, string>> = { condition: '조건부', secondary_attack: '추가 공격' };
const gateLabel = (trigger: string): string => trigger.split(' > ').map((gate) => GATE_LABELS[gate] ?? gate).join(' > ');
/**
 * Unconditional leaves add into one figure. A gated leaf is listed on its own
 * with its gate and never summed in: it may not happen on a given cast.
 */
const damageText = (damage: readonly SkillSummaryDamage[], splashRadiusM?: number): string => {
  if (damage.length === 0) return '직접 피해 없음';
  const always = damage.filter((leaf) => leaf.trigger === undefined);
  const gated = damage.filter((leaf) => leaf.trigger !== undefined);
  const hitsText = (hits: number | undefined): string => (hits !== undefined && hits > 1 ? ` × ${hits}회` : '');
  const splashText = (leaf: SkillSummaryDamage): string => leaf.splashPerHit === undefined || splashRadiusM === undefined ? '' : ` (주변 ${round(splashRadiusM, 2)}m에 ${round(leaf.splashPerHit)})`;
  const alwaysText = always.map((leaf) => `${round(leaf.perHit)}${splashText(leaf)}${hitsText(leaf.hits)}`).join(' + ');
  const gatedText = gated.map((leaf) => `(${gateLabel(leaf.trigger!)}) ${round(leaf.perHit)}${hitsText(leaf.hits)}`).join(' · ');
  return [alwaysText, gatedText].filter((part) => part !== '').join(' · ');
};

/** One owned skill on one line. The server computed every number; this only words them. */
export function compactSummaryText(summary: SkillSummaryView): string {
  if (!summary.usableWithCurrentWeapon || !summary.timing || !summary.now) return '현재 무기로 사용할 수 없음';
  const total = summary.now.expectedCastDamage === undefined ? '' : ` · 시전 1회 기대 피해 ${round(summary.now.expectedCastDamage)}`;
  return `쿨다운 ${seconds(summary.timing.cooldownS)} · 마나 ${round(summary.now.manaCost)}${total} · 타격당 피해 ${damageText(summary.now.damage, summary.now.splashRadiusM)}`;
}

export const CRAFT_STEPS = ['SOURCE', 'SHAPE', 'EFFECT', 'POWER', 'CONFIRM'] as const;
export type CraftStep = (typeof CRAFT_STEPS)[number];

/** Which axes a step is responsible for. */
const STEP_AXES: Readonly<Record<CraftStep, readonly CraftAxis[]>> = {
  SOURCE: ['source'],
  // Always complete: an unchosen shape is 단일, the catalog's first shape.
  SHAPE: [],
  EFFECT: ['effect'],
  POWER: [],
  CONFIRM: [],
};

export type CraftPhase =
  | { readonly kind: 'EDITING' }
  | { readonly kind: 'SUBMITTING' }
  | { readonly kind: 'CRAFTED'; readonly skillId: string; readonly name: string; readonly sacrificed: boolean; readonly summary: SkillSummaryView }
  | { readonly kind: 'FAILED'; readonly message: string };

export interface CraftSummaryLine {
  readonly label: string;
  readonly value: string;
}

/** What the server returned for a successful craft. Nothing is invented here. */
export interface CraftedSkillView {
  readonly skillId: string;
  readonly name: string;
  readonly sacrificed: boolean;
  readonly summary: SkillSummaryView;
}

/** The preview card's state. `summary` is the last answer that is still current enough to show. */
export interface CraftPreviewState {
  readonly summary?: SkillSummaryView;
  readonly loading: boolean;
  readonly error?: string;
}

/** The selection as the server takes it. */
export interface CraftSelectionBody {
  source: string;
  effect: string;
  shape?: string;
  shapeParameters?: Record<string, number>;
  /** Status option ids with the share of the power each takes. */
  statuses?: Record<string, number>;
}

export class SkillCraftingModel {
  private catalog: readonly CraftOptionView[] = [];
  private readonly chosen = new Map<CraftAxis, string>();
  private step: CraftStep = 'SOURCE';
  /** Slider values of the chosen shape by parameter id; absent ids are at their default. */
  private readonly shapeValues = new Map<string, number>();
  /** The statuses the player added, by option id, with the share of the power each takes. */
  private readonly statusShares = new Map<string, number>();
  private phase: CraftPhase = { kind: 'EDITING' };
  private name = '';
  private power = DEFAULT_CRAFT_POWER_MULTIPLE;
  private previewToken = 0;
  private preview: CraftPreviewState = { loading: false };

  /** The chosen source option's cap, as the server reported it; absent until a source is chosen. */
  private maxPower(): number | undefined {
    const optionId = this.chosen.get('source');
    return this.catalog.find((option) => option.axis === 'source' && option.optionId === optionId)?.maxPowerMultiple;
  }

  /** Slider bounds: every value on it is one the server's cap allows. */
  powerRange(): { readonly min: number; readonly max: number; readonly step: number } {
    const max = this.maxPower() === undefined ? CRAFT_POWER_MIN_MULTIPLE : Math.floor(this.maxPower()! / CRAFT_POWER_STEP) * CRAFT_POWER_STEP;
    return { min: CRAFT_POWER_MIN_MULTIPLE, max: Math.max(CRAFT_POWER_MIN_MULTIPLE, max), step: CRAFT_POWER_STEP };
  }

  powerMultiple(): number { return this.power; }

  setPower(value: number): void {
    if (!Number.isFinite(value)) return;
    this.power = this.clampPower(value);
    this.invalidatePreview();
  }

  /** A preset is a point on the same scale; one the selected source's cap does not reach is refused. */
  presetAvailable(preset: CraftPowerPreset): boolean {
    return CRAFT_POWER_PRESETS[preset] <= this.powerRange().max;
  }

  selectPreset(preset: CraftPowerPreset): boolean {
    if (!this.presetAvailable(preset)) return false;
    this.power = CRAFT_POWER_PRESETS[preset];
    this.invalidatePreview();
    return true;
  }

  /** A power chosen under another source's cap must not outlive it; with no source chosen there is no cap to hold to. */
  private reclampToSource(): void {
    if (this.maxPower() !== undefined) this.power = this.clampPower(this.power);
  }

  private clampPower(value: number): number {
    const { min, max, step } = this.powerRange();
    return Math.min(max, Math.max(min, Math.round(value / step) * step));
  }

  setCatalog(options: readonly CraftOptionView[]): void {
    this.catalog = options;
    // A selection that is no longer offered must not survive a catalog change;
    // it would be submitted and refused by the server for reasons the player
    // cannot see on screen.
    for (const [axis, optionId] of [...this.chosen]) {
      if (!options.some((option) => option.axis === axis && option.optionId === optionId && option.availability === 'AVAILABLE')) {
        this.chosen.delete(axis);
        if (axis === 'shape') this.shapeValues.clear();
        this.invalidatePreview();
      }
    }
    for (const optionId of [...this.statusShares.keys()]) {
      if (!options.some((option) => option.axis === 'status' && option.optionId === optionId && option.availability === 'AVAILABLE')) this.statusShares.delete(optionId);
    }
    this.dropShapeUnavailableForSource();
    this.reclampToSource();
  }

  /**
   * The share range each status crafts at for the selection in `key`, as the server computed it. A range answers for the
   * selection and power it was asked about only; any other selection leaves the catalog's bound until the server answers again.
   */
  private minimums: { readonly key: string; readonly values: Readonly<Record<string, { min: number; max: number }>> } | undefined;

  /** The part of the selection a status minimum depends on: everything but the statuses. */
  private minimumsKey(): string | undefined {
    if (!(['source', 'effect'] as const).every((axis) => this.chosen.has(axis))) return undefined;
    const { statuses: _own, ...selection } = this.selection();
    return JSON.stringify([selection, this.power]);
  }

  /** The body that asks the server for the current selection's status minimums; absent until the form is complete or once they are known. */
  statusMinimumsBody(): { key: string; selection: CraftSelectionBody; powerMultiple: number } | undefined {
    const key = this.minimumsKey();
    if (key === undefined || this.minimums?.key === key) return undefined;
    const { statuses: _own, ...selection } = this.selection();
    return { key, selection, powerMultiple: this.power };
  }

  /** Whether the minimums for the current selection are still to be asked; a new status waits until they arrive. */
  statusMinimumsPending(): boolean { return this.statusMinimumsBody() !== undefined; }

  /** Takes the server's answer for `key` if it is still the current selection; a stale answer is dropped. */
  setStatusMinimums(key: string, values: Readonly<Record<string, { min: number; max: number }>>): boolean {
    if (key !== this.minimumsKey()) return false;
    this.minimums = { key, values };
    for (const [optionId, share] of [...this.statusShares]) {
      const range = values[optionId];
      if (range === undefined) this.statusShares.delete(optionId);
      else this.statusShares.set(optionId, Math.min(range.max, Math.max(range.min, share)));
    }
    this.invalidatePreview();
    return true;
  }

  /**
   * Whether a failed minimums request for `key` is worth telling the player. One that a newer request replaced, or one for a
   * selection that is no longer current, has nothing to report: the newest selection's own answer is the one that counts.
   */
  statusMinimumsFailureIsReportable(key: string, code: string | undefined): boolean {
    return key === this.minimumsKey() && code !== STATUS_MINIMUMS_SUPERSEDED_CODE;
  }

  /** The status options with the share each has now, or none when it is not added. `offered` is false when no share crafts for the selection. */
  statusRows(): readonly { readonly option: CraftOptionView; readonly parameter: CraftParameterView; readonly share: number | undefined; readonly offered: boolean }[] {
    const known = this.minimums?.key === this.minimumsKey() ? this.minimums?.values : undefined;
    return this.catalog.filter((option) => option.axis === 'status' && option.parameters?.length === 1).map((option) => {
      const range = known?.[option.optionId];
      const parameter = option.parameters![0]!;
      return {
        option, share: this.statusShares.get(option.optionId),
        parameter: range === undefined ? parameter : { ...parameter, min: Math.max(parameter.min, range.min), max: Math.min(parameter.max, range.max) },
        offered: known === undefined || range !== undefined,
      };
    });
  }

  /** Adds a status at the slider's default (or its minimum, when that is higher), or removes it. */
  toggleStatus(optionId: string): void {
    const row = this.statusRows().find((entry) => entry.option.optionId === optionId);
    if (row === undefined) return;
    if (this.statusShares.has(optionId)) this.statusShares.delete(optionId);
    else if (row.offered) this.statusShares.set(optionId, Math.min(row.parameter.max, Math.max(row.parameter.default, row.parameter.min)));
    this.invalidatePreview();
  }

  /** Sets an added status's share, clamped to the bounds the catalog sent and the range the server found. */
  setStatusShare(optionId: string, value: number): void {
    const row = this.statusRows().find((entry) => entry.option.optionId === optionId);
    if (row === undefined || !this.statusShares.has(optionId) || !Number.isFinite(value)) return;
    const { min, max, step } = row.parameter;
    this.statusShares.set(optionId, Math.min(max, Math.max(min, Math.round(Math.round(value / step) * step * 10) / 10)));
    this.invalidatePreview();
  }

  /**
   * The catalog is loaded and offers no `AVAILABLE` source: the held weapon
   * cannot craft yet, so the form shows one notice instead of dead cards. An
   * empty catalog is "not loaded", not "unsupported".
   */
  noAvailableSource(): boolean {
    return this.catalog.length > 0 && !this.catalog.some((option) => option.axis === 'source' && option.availability === 'AVAILABLE');
  }

  optionsFor(axis: CraftAxis): readonly CraftOptionView[] {
    return this.catalog.filter((option) => option.axis === axis).map((option) => this.forChosenSource(option));
  }

  /**
   * A shape option as it stands for the chosen source. The server states
   * availability per source option; before a source is chosen the shape
   * keeps its overall availability.
   */
  private forChosenSource(option: CraftOptionView): CraftOptionView {
    const source = this.chosen.get('source');
    const entry = option.axis === 'shape' && source !== undefined ? option.sourceAvailability?.[source] : undefined;
    if (entry === undefined) return option;
    const { disabledReason: _drop, ...rest } = option;
    return { ...rest, availability: entry.availability, ...(entry.disabledReason === undefined ? {} : { disabledReason: entry.disabledReason }) };
  }

  /** The shape in effect: the chosen one, else the catalog's first (단일). */
  chosenShape(): CraftOptionView | undefined {
    const shapes = this.catalog.filter((option) => option.axis === 'shape');
    const picked = this.chosen.get('shape');
    return shapes.find((option) => option.optionId === picked) ?? shapes[0];
  }

  /** The chosen shape's sliders with their current values; empty for a shape without any. */
  shapeSliders(): readonly { readonly parameter: CraftParameterView; readonly value: number }[] {
    return (this.chosenShape()?.parameters ?? []).map((parameter) => ({ parameter, value: this.shapeValues.get(parameter.id) ?? parameter.default }));
  }

  /** Sets one slider, clamped to the bounds the catalog sent (and to whole numbers for counts). */
  setShapeValue(parameterId: string, value: number): void {
    const parameter = this.chosenShape()?.parameters?.find((entry) => entry.id === parameterId);
    if (parameter === undefined || !Number.isFinite(value)) return;
    const stepped = parameter.integer ? Math.round(value) : Math.round(value / parameter.step) * parameter.step;
    this.shapeValues.set(parameterId, Math.min(parameter.max, Math.max(parameter.min, stepped)));
    this.invalidatePreview();
  }

  private shapeSelection(): { shape?: string; shapeParameters?: Record<string, number> } {
    const shape = this.chosenShape();
    if (shape === undefined) return {};
    return {
      shape: shape.optionId,
      ...((shape.parameters?.length ?? 0) === 0 ? {} : { shapeParameters: Object.fromEntries(this.shapeSliders().map(({ parameter, value }) => [parameter.id, value])) }),
    };
  }

  currentStep(): CraftStep { return this.step; }
  currentPhase(): CraftPhase { return this.phase; }
  selectionFor(axis: CraftAxis): string | undefined { return this.chosen.get(axis); }
  skillName(): string { return this.name; }

  setName(value: string): void { this.name = value; }

  /**
   * Choosing an option that is not `AVAILABLE` is refused here as well as on
   * the server. The dialog disables them, but a disabled control is a
   * presentation detail and this is the state that gets submitted.
   */
  select(axis: CraftAxis, optionId: string): boolean {
    const found = this.catalog.find((entry) => entry.axis === axis && entry.optionId === optionId);
    const option = found === undefined ? undefined : this.forChosenSource(found);
    if (!option || option.availability !== 'AVAILABLE') return false;
    if (axis === 'shape' && this.chosenShape()?.optionId !== optionId) this.shapeValues.clear();
    this.chosen.set(axis, optionId);
    if (axis === 'source') this.dropShapeUnavailableForSource();
    this.reclampToSource();
    this.invalidatePreview();
    if (this.phase.kind === 'FAILED') this.phase = { kind: 'EDITING' };
    return true;
  }

  /** A shape the newly chosen source cannot carry is dropped, back to 단일. */
  private dropShapeUnavailableForSource(): void {
    const picked = this.chosen.get('shape');
    const option = picked === undefined ? undefined : this.catalog.find((entry) => entry.axis === 'shape' && entry.optionId === picked);
    if (option !== undefined && this.forChosenSource(option).availability !== 'AVAILABLE') {
      this.chosen.delete('shape');
      this.shapeValues.clear();
    }
  }

  /** Every axis this step owns has an answer. */
  stepComplete(step: CraftStep = this.step): boolean {
    return STEP_AXES[step].every((axis) => this.chosen.has(axis));
  }

  canAdvance(): boolean { return this.step !== 'CONFIRM' && this.stepComplete(); }

  advance(): void {
    if (!this.canAdvance()) return;
    this.step = CRAFT_STEPS[CRAFT_STEPS.indexOf(this.step) + 1]!;
  }

  back(): void {
    const index = CRAFT_STEPS.indexOf(this.step);
    if (index > 0) this.step = CRAFT_STEPS[index - 1]!;
  }

  /** Submittable: every axis answered, a non-empty name, nothing in flight, and any added status checked against the current selection's minimums. */
  canCraft(): boolean {
    return this.phase.kind !== 'SUBMITTING'
      && !(this.statusShares.size > 0 && this.statusMinimumsPending())
      && this.phase.kind !== 'CRAFTED'
      && this.name.trim().length > 0
      && (Object.keys(STEP_AXES) as CraftStep[]).every((step) => this.stepComplete(step));
  }

  /** The selection to send. Throws rather than sending a partial one. */
  selection(): CraftSelectionBody {
    const source = this.chosen.get('source');
    const effect = this.chosen.get('effect');
    if (!source || !effect) throw new Error('selection is incomplete');
    return { source, effect, ...this.shapeSelection(), ...(this.statusShares.size === 0 ? {} : { statuses: Object.fromEntries(this.statusShares) }) };
  }

  previewState(): CraftPreviewState { return this.preview; }

  /** The body a preview needs; absent until the form is complete. */
  previewBody(): { selection: CraftSelectionBody; powerMultiple: number } | undefined {
    return (['source', 'effect'] as const).every((axis) => this.chosen.has(axis)) ? { selection: this.selection(), powerMultiple: this.power } : undefined;
  }

  /**
   * Starts a preview and returns its token. Only the newest token may land:
   * a slow answer for an earlier power must not overwrite the card for the
   * power now on screen.
   */
  beginPreview(): number {
    this.previewToken += 1;
    this.preview = { ...(this.preview.summary === undefined ? {} : { summary: this.preview.summary }), loading: true };
    return this.previewToken;
  }

  completePreview(token: number, summary: SkillSummaryView): boolean {
    if (token !== this.previewToken) return false;
    this.preview = { summary, loading: false };
    return true;
  }

  failPreview(token: number, message: string): boolean {
    if (token !== this.previewToken) return false;
    this.preview = { loading: false, error: message };
    return true;
  }

  /** A change that makes any in-flight or shown preview wrong for the selection. */
  private invalidatePreview(): void {
    this.previewToken += 1;
    this.preview = { loading: false };
  }

  /** The preview card's lines, from the server's summary only. */
  previewLines(summary: SkillSummaryView): readonly CraftSummaryLine[] {
    if (!summary.usableWithCurrentWeapon || !summary.timing || !summary.now || !summary.atMastery10) {
      return [{ label: '사용 가능', value: '현재 무기로는 사용할 수 없습니다' }];
    }
    const { timing, now, atMastery10 } = summary;
    const mastery = `지금(숙련 ${summary.masteryLevel})`;
    return [
      ...(summary.powerMultiple === undefined ? [] : [{ label: '위력', value: powerMultipleLabel(round(summary.powerMultiple)) }]),
      { label: '쿨다운', value: seconds(timing.cooldownS) },
      { label: '시전 준비 / 후딜', value: `${seconds(timing.startupS)} / ${seconds(timing.recoveryS)}` },
      { label: `마나 ${mastery}`, value: String(round(now.manaCost)) },
      { label: '마나 숙련 10', value: String(round(atMastery10.manaCost)) },
      ...(now.expectedCastDamage === undefined || atMastery10.expectedCastDamage === undefined ? [] : [
        { label: `시전 1회 기대 피해 ${mastery}`, value: String(round(now.expectedCastDamage)) },
        { label: '시전 1회 기대 피해 숙련 10', value: String(round(atMastery10.expectedCastDamage)) },
      ]),
      ...(summary.expectedHits === undefined ? [] : [{ label: '기대 적중 수', value: String(round(summary.expectedHits, 2)) }]),
      { label: `타격당 피해 ${mastery}`, value: damageText(now.damage, now.splashRadiusM) },
      { label: '타격당 피해 숙련 10', value: damageText(atMastery10.damage, atMastery10.splashRadiusM) },
    ];
  }

  beginCraft(): void { this.phase = { kind: 'SUBMITTING' }; }
  completeCraft(result: CraftedSkillView): void { this.phase = { kind: 'CRAFTED', ...result }; }
  failCraft(message: string): void { this.phase = { kind: 'FAILED', message }; }

  /**
   * The pre-craft summary: the player's own choices, restated.
   *
   * No damage, no cost, no cooldown. Those come only from the server's
   * preview (POWER step) or the craft response; showing a guess of ours that
   * the result then contradicts is worse than showing nothing.
   */
  summary(): readonly CraftSummaryLine[] {
    const labelFor = (axis: CraftAxis): string => {
      const optionId = this.chosen.get(axis);
      const option = this.catalog.find((entry) => entry.axis === axis && entry.optionId === optionId);
      return option?.label ?? '선택 안 됨';
    };
    const shape = this.chosenShape();
    const sliders = this.shapeSliders().map(({ parameter, value }) => `${parameter.label} ${value}${parameter.unit}`).join(', ');
    return [
      { label: '공격 방식', value: labelFor('source') },
      { label: '형태', value: shape === undefined ? '선택 안 됨' : `${shape.label}${sliders === '' ? '' : ` (${sliders})`}` },
      { label: '효과', value: labelFor('effect') },
      ...this.statusRows().flatMap(({ option, share }) => share === undefined ? [] : [{ label: option.label, value: `위력의 ${Math.round(share * 100)}%` }]),
      { label: '위력', value: powerMultipleLabel(this.power) },
    ];
  }

  /** Reset to a fresh dialog, keeping the catalog already fetched. */
  reset(): void {
    this.chosen.clear();
    this.shapeValues.clear();
    this.statusShares.clear();
    this.minimums = undefined;
    this.step = 'SOURCE';
    this.phase = { kind: 'EDITING' };
    this.name = '';
    this.power = DEFAULT_CRAFT_POWER_MULTIPLE;
    this.invalidatePreview();
  }
}
